/**
 * TabBar - bottom navigation: five business tabs (Docs since jarvis-today PH-08) and Life (the personal side).
 * Active state driven by preact-router URL.
 * Chat tab shows a WebSocket connection dot.
 * Approvals tab shows a pending-count badge.
 * Today tab shows an urgent-count dot (fed by the Today store in P3).
 */

import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { route, useRouter } from 'preact-router';
import { wsConnected } from '../core/ws/client';
import { api } from '../core/api/client';
import { lifeNewCount, refreshLife } from '../modules/life/life-store';
import styles from './TabBar.module.css';
import { loadQuestions, questionCount } from '../modules/approvals/questions-store';

/** Pending approval count - refreshed on mount + ws updates */
export const pendingCount = signal(0);

/**
 * Count of "needs you now" items on Today (deadlines inside 48h, pending
 * approvals, etc). Defaults to 0 (renders nothing - no fake data). The Today
 * store (P3) is responsible for setting this from real aggregated data; P1a
 * only lands the signal + the render wiring for the dot.
 */
export const todayUrgentCount = signal(0);

/** Refresh the pending approval count from the API */
export async function refreshPendingCount() {
  try {
    const res = await api.getApprovals('pending');
    pendingCount.value = res.total;
  } catch {
    // Silently ignore - badge just won't update
  }
  void loadQuestions();   // PH-10: open Build Desk questions count on the badge too
}

interface TabDef {
  id: string;
  label: string;
  path: string;
  icon: () => preact.JSX.Element;
}

const tabs: TabDef[] = [
  {
    id: 'today',
    label: 'Today',
    path: '/today',
    // Sun-horizon: half sun resting on a horizon line, a few rays above.
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M5.5 17a6.5 6.5 0 0113 0" />
        <line x1="2.5" y1="17" x2="21.5" y2="17" />
        <line x1="12" y1="6.5" x2="12" y2="8.5" />
        <line x1="6.3" y1="9.3" x2="7.7" y2="10.7" />
        <line x1="17.7" y1="9.3" x2="16.3" y2="10.7" />
      </svg>
    ),
  },
  {
    id: 'deals',
    label: 'Deals',
    path: '/deals',
    // Key: bow + shaft + two teeth.
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="8" cy="12" r="3.5" />
        <line x1="11.3" y1="12" x2="21" y2="12" />
        <line x1="17" y1="12" x2="17" y2="15" />
        <line x1="14" y1="12" x2="14" y2="14.5" />
      </svg>
    ),
  },
  {
    id: 'chat',
    label: 'Chat',
    path: '/',
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
  {
    id: 'approvals',
    label: 'Approvals',
    path: '/approvals',
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'docs',
    label: 'Docs',
    path: '/docs',
    // A page with a folded corner: signing, signed papers and reports (jarvis-today PH-08).
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
        <path d="M14 3v5h5" />
        <line x1="8.5" y1="13" x2="15.5" y2="13" />
        <line x1="8.5" y1="16.5" x2="13" y2="16.5" />
      </svg>
    ),
  },
  {
    id: 'life',
    label: 'Life',
    path: '/life',
    // Heart: family, home, money and the rest of his personal side (life-hub PH-01; Finance and Watches live here).
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M12 20s-7.5-4.6-7.5-10.1A4.15 4.15 0 0112 7.4a4.15 4.15 0 017.5 2.5C19.5 15.4 12 20 12 20z" />
      </svg>
    ),
  },
];

/** Pages the Life tab absorbed keep their old addresses (bookmarks, links in old messages). */
const LIFE_PATHS = ['/life', '/finance', '/watches', '/trading'];

function isTabActive(tabPath: string, currentUrl: string): boolean {
  if (tabPath === '/life') {
    return LIFE_PATHS.some((p) => currentUrl === p || currentUrl.startsWith(`${p}/`) || currentUrl.startsWith(`${p}?`));
  }
  if (tabPath === '/') {
    return currentUrl === '/' || currentUrl.startsWith('/chat');
  }
  return currentUrl.startsWith(tabPath);
}

export function TabBar() {
  const [routerState] = useRouter();
  const currentUrl = routerState?.url ?? '/';

  // Fetch pending count on mount
  useEffect(() => {
    refreshPendingCount();
  }, []);

  // Life's "something new" dot: read on mount, then again when the app comes back to the front (at most every 5 min).
  useEffect(() => {
    let last = Date.now();
    refreshLife();
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 5 * 60_000) return;
      last = Date.now();
      refreshLife();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  return (
    <nav class={styles.tabBar}>
      {tabs.map((tab) => {
        const active = isTabActive(tab.path, currentUrl);
        return (
          <button
            key={tab.id}
            class={`${styles.tab} ${active ? styles.tabActive : ''}`}
            onClick={() => route(tab.path)}
            aria-label={tab.label}
            aria-current={active ? 'page' : undefined}
          >
            {/* Chat tab gets the status dot wrapper; Today gets the urgent dot */}
            {tab.id === 'chat' ? (
              <span class={styles.iconWrap}>
                {tab.icon()}
                <span
                  class={`${styles.statusDot} ${
                    wsConnected.value ? '' : styles.statusDotDisconnected
                  }`}
                  title={wsConnected.value ? 'Connected' : 'Disconnected'}
                />
              </span>
            ) : tab.id === 'life' && lifeNewCount.value > 0 ? (
              <span class={styles.iconWrap}>
                {tab.icon()}
                <span class={styles.newDot} title="Something new in Life" />
              </span>
            ) : tab.id === 'today' && todayUrgentCount.value > 0 ? (
              <span class={styles.iconWrap}>
                {tab.icon()}
                <span class={styles.urgentDot} title="Needs your attention" />
              </span>
            ) : (
              tab.icon()
            )}

            <span class={styles.label}>{tab.label}</span>

            {/* Approvals badge */}
            {tab.id === 'approvals' && pendingCount.value + questionCount.value > 0 && (
              <span class={styles.badge}>{pendingCount.value + questionCount.value}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
